import { useEffect, useMemo, useRef } from 'react';
import { LuArrowLeft, LuLock, LuMessageSquare } from 'react-icons/lu';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { SmsMessage } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { useHouseholdRoutePaths, useRequiredParams } from 'utils';
import { PageSpinner } from 'widgets';

import { useTextsQuery } from './text-queries';
import {
    counterparties,
    counterpartyNumber,
    groupByDay,
    initials,
    isOutbound,
    messageTime,
    partyName,
    senderColor,
} from './text-utils';

/** Small circular avatar with initials, tinted by a deterministic per-sender color. */
const Avatar = ({ name, colorKey, className = '' }: { name: string; colorKey?: string; className?: string }) => {
    const color = senderColor(colorKey);
    return (
        <div
            className={`flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-white text-[11px] font-semibold ${className}`}
            style={{ background: `${color}29`, color }}
        >
            {initials(name)}
        </div>
    );
};

const MessageBubble = ({
    message,
    showSender,
    highlighted,
}: {
    message: SmsMessage;
    showSender: boolean;
    highlighted: boolean;
}) => {
    const outbound = isOutbound(message);
    const name = partyName(message.fromName, message.fromNumber);
    return (
        <div
            data-msgid={message.smsMessageGUID}
            className={`flex items-end gap-2.5 ${outbound ? 'flex-row-reverse' : 'flex-row'} ${
                highlighted ? '-mx-3 rounded-xl bg-sky-50/70 px-3 py-2.5' : ''
            }`}
        >
            {showSender ? <Avatar name={name} colorKey={message.fromNumber} /> : <div className="size-8 shrink-0" />}
            <div className={`flex max-w-[80%] min-w-0 flex-col ${outbound ? 'items-end' : 'items-start'}`}>
                {showSender && (
                    <div className="mb-1 text-xs font-semibold" style={{ color: senderColor(message.fromNumber) }}>
                        {name}
                    </div>
                )}
                <div
                    className={`rounded-2xl border px-3.5 py-2.5 text-sm leading-relaxed break-words whitespace-pre-wrap ${
                        outbound
                            ? 'bg-primary/10 rounded-br-sm border-transparent'
                            : 'border-border rounded-bl-sm bg-white'
                    } ${highlighted ? '!border-amber-700' : ''}`}
                >
                    {message.message || <span className="text-muted-foreground italic">[No content]</span>}
                </div>
                <div className={`text-muted-foreground/80 mt-1 text-[11px] ${highlighted ? 'font-semibold' : ''}`}>
                    {messageTime(message)}
                </div>
            </div>
        </div>
    );
};

export const HouseholdTextsConversation = () => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const [searchParams] = useSearchParams();
    const selectedMsgId = searchParams.get('msg');
    const navigate = useNavigate();
    const { MessagesTabRoutePath } = useHouseholdRoutePaths();
    const scrollRef = useRef<HTMLDivElement>(null);

    const textsQuery = useTextsQuery(globalAccountGUID);
    const messages = useMemo(() => textsQuery.data ?? [], [textsQuery.data]);
    const dayGroups = useMemo(() => groupByDay(messages), [messages]);
    const participants = useMemo(() => counterparties(messages), [messages]);

    const backToList = () => navigate(MessagesTabRoutePath(globalAccountGUID));

    // Scroll the selected message into view once data is loaded; otherwise jump to the latest.
    useEffect(() => {
        const container = scrollRef.current;
        if (!container || messages.length === 0) return;
        const target = selectedMsgId ? container.querySelector<HTMLElement>(`[data-msgid="${selectedMsgId}"]`) : null;
        if (target) {
            target.scrollIntoView({ block: 'center' });
        } else {
            container.scrollTop = container.scrollHeight;
        }
    }, [messages, selectedMsgId, dayGroups]);

    if (textsQuery.isLoading) {
        return <PageSpinner />;
    }

    if (messages.length === 0) {
        return (
            <div className="text-muted-foreground p-8 text-center">
                <Button variant="ghost" size="icon" onClick={backToList} className="mb-2">
                    <LuArrowLeft className="size-5" />
                </Button>
                <p>No text messages found for this household.</p>
            </div>
        );
    }

    const subLabel = `${participants.length} ${participants.length === 1 ? 'contact' : 'contacts'} · ${
        messages.length
    } ${messages.length === 1 ? 'message' : 'messages'}`;

    return (
        <div className="flex flex-col gap-4 p-4">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={backToList}>
                    <LuArrowLeft className="size-5" />
                </Button>
                <div className="flex -space-x-2">
                    {participants.slice(0, 7).map((message) => (
                        <Avatar
                            key={message.smsMessageGUID}
                            name={partyName(message.fromName, message.fromNumber)}
                            colorKey={counterpartyNumber(message)}
                        />
                    ))}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="truncate text-lg font-bold">Text messages</div>
                    <div className="text-muted-foreground text-sm">{subLabel}</div>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-sky-100 px-3 py-1 text-xs text-sky-800">
                    <LuMessageSquare className="size-3" /> SMS
                </span>
            </div>

            {/* conversation */}
            <div className="bg-card flex max-h-[660px] min-w-0 flex-1 flex-col overflow-hidden rounded-xl border">
                <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 pb-4">
                    {dayGroups.map((group) => (
                        <div key={group.label}>
                            <div className="sticky top-0 z-10 flex justify-center py-3">
                                <span className="bg-muted text-muted-foreground rounded-full border px-3 py-1 text-xs tracking-wide uppercase">
                                    {group.label}
                                </span>
                            </div>
                            <div className="flex flex-col gap-2.5">
                                {group.items.map((message, index) => {
                                    const prev = group.items[index - 1];
                                    const showSender =
                                        !prev ||
                                        prev.fromNumber !== message.fromNumber ||
                                        isOutbound(prev) !== isOutbound(message);
                                    return (
                                        <MessageBubble
                                            key={message.smsMessageGUID}
                                            message={message}
                                            showSender={showSender}
                                            highlighted={message.smsMessageGUID === selectedMsgId}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="text-muted-foreground bg-muted/40 flex items-center gap-2 border-t px-6 py-3 text-xs">
                    <LuLock className="size-3.5" />
                    <span>Synced and read-only, captured for compliance and recordkeeping.</span>
                </div>
            </div>
        </div>
    );
};
